// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 32-knowledge.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { ProposalRow } from '@/components/knowledge/ProposalRow';
import { ProposalReview } from '@/components/knowledge/ProposalReview';
import { formatRelativeDate } from '@/components/knowledge/util';
import { useScopeResolvers } from '@/components/knowledge/useScopeResolvers';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type { KnowledgeProposal } from '@greenlight/contract';

// ---------------------------------------------------------------------------
// proposalEntityLink helper
// ---------------------------------------------------------------------------

/** Deep-link a proposal to the entity it edits (docs/28 § /knowledge → proposals). */
function proposalEntityLink(p: KnowledgeProposal): { to: string; label: string } | null {
  if (p.scope === 'integration' && p.integrationId)
    return { to: `/integrations?integration=${p.integrationId}`, label: 'Open integration' };
  if (p.scope === 'app' && p.appId) return { to: `/apps/${p.appId}`, label: 'Open app' };
  return null;
}

// ---------------------------------------------------------------------------
// ProposalsCard (main export)
// ---------------------------------------------------------------------------

export interface ProposalsCardProps {
  userRole: string | undefined;
}

export function ProposalsCard({ userRole }: ProposalsCardProps): React.JSX.Element {
  const [openProposalId, setOpenProposalId] = React.useState<string | null>(null);

  const { data, refetch, isLoading, isError } = api.knowledge.listProposals.useQuery(
    ['knowledge-proposals'],
    {},
  );
  const queryClient = useQueryClient();
  const resolve = useScopeResolvers();

  const pending = (data?.body.items ?? []).filter((p) => p.status === 'pending');
  const open = pending.find((p) => p.id === openProposalId) ?? null;

  return (
    <section
      className="rounded-gl-lg border border-border bg-card overflow-hidden"
      data-testid="knowledge-proposals"
    >
      <div className="flex items-center gap-2 px-4 md:px-5 h-11 bg-muted/50 border-b border-border">
        <span
          className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--status-amber))]"
          aria-hidden="true"
        />
        <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] font-medium text-foreground">
          Proposed edits
        </span>
        <span
          className="font-mono text-[10.5px] text-muted-foreground"
          data-testid="proposal-count"
        >
          {pending.length}
        </span>
      </div>
      {isLoading ? (
        <div className="space-y-2 px-4 md:px-5 py-4">
          <Skeleton className="h-9 w-full rounded-gl" />
          <Skeleton className="h-9 w-full rounded-gl" />
          <Skeleton className="h-9 w-3/4 rounded-gl" />
        </div>
      ) : isError ? (
        <div className="px-4 md:px-5 py-4" data-testid="proposals-error">
          <Alert variant="destructive">
            <AlertDescription>Couldn't load proposals. Retry in a moment.</AlertDescription>
          </Alert>
        </div>
      ) : pending.length === 0 ? (
        <p
          className="px-4 md:px-5 py-4 text-sm text-muted-foreground"
          data-testid="proposals-empty"
        >
          No proposals waiting. Agents propose updates from inside a builder session.
        </p>
      ) : (
        <ul className="divide-y divide-border" data-testid="proposals-list">
          {pending.map((p) => {
            const { scopeLabel, tile } = resolve({
              scope: p.scope,
              integrationId: p.integrationId,
              appId: p.appId,
            });
            const entity = proposalEntityLink(p);
            const isAgent = p.proposedBy.agentSessionId != null;
            return (
              <ProposalRow
                key={p.id}
                testId={`proposal-${p.id}`}
                title={p.title}
                scopeLabel={scopeLabel}
                isNewEntry={p.entryId == null}
                isAgent={isAgent}
                proposerLabel={isAgent ? 'Claude' : (p.proposedBy.email ?? 'Unknown')}
                relativeTime={formatRelativeDate(p.proposedAt)}
                rationale={p.rationale}
                tile={tile}
                entityLink={
                  entity
                    ? { to: entity.to, label: entity.label, testId: `proposal-entity-link-${p.id}` }
                    : undefined
                }
                onReview={() => setOpenProposalId(p.id)}
              />
            );
          })}
        </ul>
      )}
      <ProposalReview
        proposal={open}
        userRole={userRole}
        onClose={() => setOpenProposalId(null)}
        onReviewed={(affectedEntryId) => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ['knowledge-entries'] });
          if (affectedEntryId) {
            queryClient.invalidateQueries({ queryKey: ['knowledge-entry', affectedEntryId] });
            queryClient.invalidateQueries({
              queryKey: ['knowledge-entry-history', affectedEntryId],
            });
          }
          setOpenProposalId(null);
        }}
      />
    </section>
  );
}
